// -- type definitions --

interface LexicalPlainTextNode {
  readonly type?: string;
  readonly text?: string;
  readonly children?: readonly LexicalPlainTextNode[];
}

// -- helper functions --

/**
 * Check if a string is a valid ISO date string.
 */
function isISODateString(str: string): boolean {
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
  return isoDateRegex.test(str);
}

/**
 * Convert an ISO date string to a long date string.
 */
function convertToLocaleLongDate(str: string, locale: string | undefined = undefined): string | null {
  if (!isISODateString(str)) {
    return null;
  }
  const date = new Date(str);
  const resolvedLocale = locale || navigator.language || 'fr-FR';
  return date.toLocaleDateString(resolvedLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}


/**
 * Recursively extract plain text from a Lexical node.
 */
function extractPlainTextFromLexicalNode(node: LexicalPlainTextNode): string {
  if (node.type === 'text' && typeof node.text === 'string') {
    return node.text;
  }

  if (!node.children?.length) {
    return '';
  }

  return node.children.map(extractPlainTextFromLexicalNode).join('');
}

/**
 * Extract plain text from a list of Lexical nodes.
 */
function extractPlainTextFromLexicalNodes(
  nodes: readonly LexicalPlainTextNode[]
): string {
  const blocks = nodes
    .map((node) => extractPlainTextFromLexicalNode(node))
    .filter((block) => block.length > 0);

  return blocks.join('\n');
}


// -- main functions --

// TODO: adapt to the report engine's rich text content format when supported.

/**
 * Walk Lexical JSON and return plain text.
 * 
 * * If the content is not Lexical JSON, return the original content.
 * * This function will stop being necessary when the report engine supports rich text content.
 */
export function plainTextFromLexicalJson(value: string): string {
  if (!value || value.trim() === '') {
    return '';
  }

  try {
    const parsed = JSON.parse(value) as {
      root?: { children?: readonly LexicalPlainTextNode[] };
    };
    if (parsed.root?.children) {
      return extractPlainTextFromLexicalNodes(parsed.root.children);
    }
  } catch {
    // Stored value is plain text, not Lexical JSON.
  }

  return value;
}

/** 
 * Normalize stored text widget content.
 * 
 * * If the content is a date, convert it to a long date string.
 * * In any case, strip out formatting to keep only the plain text. This is necessary
 *   to ensure compatibility with the report engine.
 */
export function normalizeTextWidgetExportContent(textValue: string, locale: string | undefined = undefined): string {
  const plainText = plainTextFromLexicalJson(textValue);
  return convertToLocaleLongDate(plainText, locale) ?? plainText;
}

/** 
 * Read text widget's content, as plain text, straight from the DOM.
 * 
 * Will stop being necessary when the report engine supports rich text content.
 */
export function readPlainTextFromTextWidgetDom(widgetId: string, locale: string | undefined = undefined): string {
  const target = document.querySelector(`#widget-${widgetId}`);
  if (!target) {
    return '<&lt;texte non fourni ou non trouvé&gt;>';
  }

  let textElement = target.querySelector('div[data-lexical-editor="true"]');
  if (!textElement) {
    textElement = target.querySelector('.value');
  }
  if (!textElement) {
    return '<&lt;texte non fourni ou non trouvé&gt;>';
  }

  const textContent = `${
    (textElement as HTMLInputElement).value || textElement.textContent
  }`;
  return convertToLocaleLongDate(textContent, locale) ?? textContent;
}
